import React, { useState, useEffect, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Users,
  Download,
  Hourglass,
  RefreshCcw,
  Search,
  X,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Inbox,
  Heart,
  MessageCircle,
  UserPlus,
  Mail,
  Film,
  FileText,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Globe,
  List,
  User,
  Menu,
} from 'lucide-react';
import Spinner from 'components/common/Spinner';
import {
  getDetailedUsers,
  exportUsersReport,
  DetailedUser,
  DetailedUsersResponse,
  DetailedUsersQueryParams,
} from 'services/admin/accountsAnalysisService';
import UserDetailsModal from './modals/UserDetailsModal';
import UserAccountsModal from './modals/UserAccountsModal';
import ActivityLogsModal from './modals/ActivityLogsModal';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('AccountsAnalysis Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center"
          style={{ background: '#0F172B' }}
        >
          <div
            className="rounded-lg p-8 max-w-md text-center"
            style={{
              background: '#1D293D',
              border: '1px solid #314158',
              boxShadow: '0px 1px 2px rgba(2, 6, 24, 0.5)',
            }}
          >
            <AlertTriangle size={36} color="#F87171" style={{ margin: '0 auto 16px' }} />
            <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#E2E8F0', marginBottom: '8px' }}>
              Something went wrong
            </h2>
            <p style={{ fontSize: '14px', color: '#90A1B9', marginBottom: '16px' }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                background: '#2B7FFF',
                color: '#FFFFFF',
                borderRadius: '6px',
                border: 'none',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const AccountsAnalysisContent: React.FC = () => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { toggleSidebar } = useSidebar();

  // Data state
  const [users, setUsers] = useState<DetailedUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<DetailedUsersResponse['summary'] | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [limit] = useState<number>(20);

  // Filter state
  const [search, setSearch] = useState<string>('');
  const [subscriptionType, setSubscriptionType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [sortBy, setSortBy] = useState<'created_on' | 'email' | 'accountsCount'>('created_on');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Modal state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showAccountsModal, setShowAccountsModal] = useState<boolean>(false);
  const [showActivityLogsModal, setShowActivityLogsModal] = useState<boolean>(false);

  // Export state
  const [exporting, setExporting] = useState<boolean>(false);

  // Hover state for table rows
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  // Hover state for action buttons
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);

  // Debounce search
  const [searchDebounce, setSearchDebounce] = useState<string>('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: DetailedUsersQueryParams = {
        page: currentPage,
        limit,
        sortBy,
        sortOrder,
      };

      if (searchDebounce) params.search = searchDebounce;
      if (subscriptionType) params.subscriptionType = subscriptionType;
      if (status) params.status = status;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await getDetailedUsers(params);
      setUsers(response.users);
      setTotalPages(response.pagination.totalPages);
      setTotalUsers(response.pagination.total);
      setSummary(response.summary);
    } catch (err: any) {
      const errorMessage = err.message || t('admin.accountsAnalysis.errors.fetchFailed') || 'Failed to fetch data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [currentPage, limit, searchDebounce, subscriptionType, status, startDate, endDate, sortBy, sortOrder, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchDebounce, subscriptionType, status, startDate, endDate, sortBy, sortOrder]);

  // Handle sort
  const handleSort = (field: 'created_on' | 'email' | 'accountsCount') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearch('');
    setSubscriptionType('');
    setStatus('');
    setStartDate('');
    setEndDate('');
    setSortBy('created_on');
    setSortOrder('desc');
  };

  // Export report
  const handleExport = async () => {
    try {
      setExporting(true);
      const params: { startDate?: string; endDate?: string } = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      await exportUsersReport(params);
      toast.success(t('admin.accountsAnalysis.exportSuccess') || 'Report exported successfully');
    } catch (err: any) {
      const errorMessage = err.message || t('admin.accountsAnalysis.errors.exportFailed') || 'Failed to export report';
      toast.error(errorMessage);
    } finally {
      setExporting(false);
    }
  };

  // Open modals
  const openDetailsModal = (userId: string) => {
    setSelectedUserId(userId);
    setShowDetailsModal(true);
  };

  const openAccountsModal = (userId: string) => {
    setSelectedUserId(userId);
    setShowAccountsModal(true);
  };

  const openActivityLogsModal = (userId: string) => {
    setSelectedUserId(userId);
    setShowActivityLogsModal(true);
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status badge style
  const getStatusBadgeStyle = (userStatus: string): React.CSSProperties => {
    switch (userStatus.toLowerCase()) {
      case 'active':
        return {
          background: isDark ? '#0D2818' : '#ECFDF5',
          color: isDark ? '#6EE7B7' : '#15803D',
          border: `1px solid ${isDark ? '#065F46' : '#BBF7D0'}`,
        };
      case 'trial':
        return {
          background: isDark ? '#1E1338' : '#FAF5FF',
          color: isDark ? '#C4B5FD' : '#7E22CE',
          border: `1px solid ${isDark ? '#4C1D95' : '#E9D5FF'}`,
        };
      case 'expired':
        return {
          background: isDark ? '#2A1215' : '#FEF2F2',
          color: isDark ? '#FCA5A5' : '#DC2626',
          border: `1px solid ${isDark ? '#7F1D1D' : '#FECACA'}`,
        };
      case 'cancelled':
        return {
          background: isDark ? '#1D293D' : '#F9FAFB',
          color: isDark ? '#90A1B9' : '#6B7280',
          border: `1px solid ${isDark ? '#314158' : '#E5E7EB'}`,
        };
      case 'pending':
        return {
          background: isDark ? '#2A1F0F' : '#FEFCE8',
          color: isDark ? '#FDE68A' : '#A16207',
          border: `1px solid ${isDark ? '#78350F' : '#FEF08A'}`,
        };
      default:
        return {
          background: isDark ? '#1D293D' : '#F9FAFB',
          color: isDark ? '#90A1B9' : '#6B7280',
          border: `1px solid ${isDark ? '#314158' : '#E5E7EB'}`,
        };
    }
  };

  // Get subscription badge style
  const getSubscriptionBadgeStyle = (subType: string): React.CSSProperties => {
    switch (subType.toLowerCase()) {
      case 'pending':
        return {
          background: isDark ? '#2A1F0F' : '#FEFCE8',
          color: isDark ? '#FDE68A' : '#A16207',
        };
      case 'trial':
        return {
          background: isDark ? '#1E1338' : '#FAF5FF',
          color: isDark ? '#C4B5FD' : '#7E22CE',
        };
      case 'starter':
        return {
          background: isDark ? '#0D2818' : '#ECFDF5',
          color: isDark ? '#6EE7B7' : '#15803D',
        };
      case 'pro':
        return {
          background: isDark ? '#132044' : '#EFF6FF',
          color: isDark ? '#93C5FD' : '#2563EB',
        };
      case 'elite':
        return {
          background: isDark ? '#2A1215' : '#FEF2F2',
          color: isDark ? '#FCA5A5' : '#DC2626',
        };
      default:
        return {
          background: isDark ? '#1D293D' : '#F9FAFB',
          color: isDark ? '#90A1B9' : '#6B7280',
        };
    }
  };

  // Color tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#FFFFFF';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';
  const inputBg = isDark ? '#1D293D' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#D1D5DB';
  const inputText = isDark ? '#E2E8F0' : '#374151';
  const tableBg = isDark ? '#0F172B' : '#FFFFFF';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableRowHoverBg = isDark ? '#162456' : '#F8FAFC';
  const tableBorderColor = isDark ? '#1D293D' : '#F1F5F9';
  const textPrimary = isDark ? '#E2E8F0' : '#111827';
  const textSecondary = isDark ? '#90A1B9' : '#6B7280';
  const textMuted = isDark ? '#64748B' : '#9CA3AF';

  // Button styles
  const exportBtnBg = isDark ? '#022C22' : '#F0FDF4';
  const exportBtnColor = isDark ? '#5EE9B5' : '#16A34A';
  const exportBtnHoverBg = isDark ? '#033A2E' : '#DCFCE7';
  const refreshBtnBg = isDark ? '#162456' : '#EFF6FF';
  const refreshBtnColor = isDark ? '#51A2FF' : '#2B7FFF';
  const refreshBtnHoverBg = isDark ? '#1C398E' : '#DBEAFE';

  // Summary card configs
  const summaryCardStyles = {
    totalUsers: {
      iconBg: isDark ? '#132044' : '#EFF6FF',
      iconColor: isDark ? '#93C5FD' : '#2B7FFF',
    },
    activeUsers: {
      iconBg: isDark ? '#0D2818' : '#ECFDF5',
      iconColor: isDark ? '#6EE7B7' : '#16A34A',
    },
    trialUsers: {
      iconBg: isDark ? '#1E1338' : '#FAF5FF',
      iconColor: isDark ? '#C4B5FD' : '#9333EA',
    },
    expiredUsers: {
      iconBg: isDark ? '#2A1215' : '#FEF2F2',
      iconColor: isDark ? '#FCA5A5' : '#DC2626',
    },
  };

  // Activity icon colors
  const activityColors = {
    likes: isDark ? '#FCA5A5' : '#F87171',
    comments: isDark ? '#51A2FF' : '#60A5FA',
    follows: isDark ? '#5EE9B5' : '#4ADE80',
    messages: isDark ? '#C27AFF' : '#A855F7',
    stories: isDark ? '#FBBF24' : '#F59E0B',
    posts: isDark ? '#2DD4BF' : '#14B8A6',
  };

  // Accounts colors
  const activeColor = isDark ? '#5EE9B5' : '#16A34A';
  const lockedColor = isDark ? '#FCA5A5' : '#DC2626';
  const successColor = isDark ? '#5EE9B5' : '#16A34A';

  // Action button styles
  const actionBtnStyles = {
    details: {
      color: isDark ? '#51A2FF' : '#2563EB',
      hoverBg: isDark ? '#162456' : '#EFF6FF',
    },
    accounts: {
      color: isDark ? '#5EE9B5' : '#16A34A',
      hoverBg: isDark ? '#022C22' : '#F0FDF4',
    },
    logs: {
      color: isDark ? '#C27AFF' : '#9333EA',
      hoverBg: isDark ? '#3C0366' : '#FAF5FF',
    },
  };

  // Pagination styles
  const paginationBtnBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBtnBorder = isDark ? '#314158' : '#D1D5DB';
  const paginationBtnColor = isDark ? '#E2E8F0' : '#374151';
  const paginationBtnHoverBg = isDark ? '#162456' : '#F9FAFB';
  const paginationBtnDisabledOpacity = 0.5;

  // Input common style
  const inputStyle: React.CSSProperties = {
    background: inputBg,
    border: `1px solid ${inputBorder}`,
    color: inputText,
    fontSize: '12px',
    borderRadius: '4px',
    outline: 'none',
  };

  return (
    <div
      className="flex flex-col items-start flex-1 min-h-0 font-geist p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{
        gap: '8px',
        background: containerBg,
      }}
    >
      {/* Main content wrapper */}
      <div
        className="flex flex-col flex-1 min-h-0 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{
          background: mainBg,
          boxShadow: mainShadow,
        }}
      >
        {/* Header */}
        <div
          className="flex flex-col justify-center items-start w-full flex-shrink-0 rounded-none lg:rounded-t-2xl"
          style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${headerBorder}`,
            background: mainBg,
          }}
        >
          <div className="flex flex-row items-center justify-between w-full" style={{ gap: '8px' }}>
            <div className="flex items-center gap-3 min-w-0">
              <button
                className="lg:hidden flex items-center justify-start flex-shrink-0"
                style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                onClick={toggleSidebar}
              >
                <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
              </button>
              <div
                className="hidden lg:flex items-center justify-center flex-shrink-0"
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '12px',
                  background: iconContainerBg,
                }}
              >
                <Users size={20} color={iconColor} />
              </div>
              <div className="min-w-0">
                <h1
                  className="font-bold truncate"
                  style={{ fontSize: '20px', lineHeight: '28px', color: titleColor, margin: 0 }}
                >
                  {t('admin.accountsAnalysis.title') || 'Accounts Analysis'}
                </h1>
                <p
                  className="hidden md:block"
                  style={{ fontSize: '12px', color: subtitleColor, margin: 0 }}
                >
                  {t('admin.accountsAnalysis.subtitle') || 'Comprehensive user management and activity analysis'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-2 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: exportBtnBg,
                  color: exportBtnColor,
                  border: 'none',
                  cursor: exporting ? 'not-allowed' : 'pointer',
                }}
                onMouseEnter={(e) => {
                  if (!exporting) (e.currentTarget as HTMLElement).style.background = exportBtnHoverBg;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = exportBtnBg;
                }}
              >
                {exporting ? (
                  <>
                    <Hourglass size={14} className="animate-spin" />
                    <span className="hidden sm:inline">{t('admin.accountsAnalysis.exporting') || 'Exporting...'}</span>
                  </>
                ) : (
                  <>
                    <Download size={14} />
                    <span className="hidden sm:inline">{t('common.export') || 'Export'}</span>
                  </>
                )}
              </button>
              <button
                onClick={fetchUsers}
                className="flex items-center gap-2 transition-colors text-xs font-medium"
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  background: refreshBtnBg,
                  color: refreshBtnColor,
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = refreshBtnHoverBg;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = refreshBtnBg;
                }}
              >
                <RefreshCcw size={14} />
                <span className="hidden sm:inline">{t('common.refresh') || 'Refresh'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className={`flex-1 min-h-0 overflow-y-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}
          style={{ padding: '16px' }}
        >
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
              <div
                className="rounded-lg p-4"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: mainShadow,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: summaryCardStyles.totalUsers.iconBg }}
                  >
                    <Users size={20} color={summaryCardStyles.totalUsers.iconColor} />
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                      {t('admin.accountsAnalysis.totalUsers') || 'Total Users'}
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: textPrimary, margin: 0 }}>
                      {summary.totalUsers}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="rounded-lg p-4"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: mainShadow,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: summaryCardStyles.activeUsers.iconBg }}
                  >
                    <CheckCircle size={20} color={summaryCardStyles.activeUsers.iconColor} />
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                      {t('admin.accountsAnalysis.activeUsers') || 'Active Users'}
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: textPrimary, margin: 0 }}>
                      {summary.activeUsers}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="rounded-lg p-4"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: mainShadow,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: summaryCardStyles.trialUsers.iconBg }}
                  >
                    <Hourglass size={20} color={summaryCardStyles.trialUsers.iconColor} />
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                      {t('admin.accountsAnalysis.trialUsers') || 'Trial Users'}
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: textPrimary, margin: 0 }}>
                      {summary.trialUsers}
                    </p>
                  </div>
                </div>
              </div>
              <div
                className="rounded-lg p-4"
                style={{
                  background: cardBg,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: mainShadow,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: summaryCardStyles.expiredUsers.iconBg }}
                  >
                    <XCircle size={20} color={summaryCardStyles.expiredUsers.iconColor} />
                  </div>
                  <div>
                    <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                      {t('admin.accountsAnalysis.expiredUsers') || 'Expired Users'}
                    </p>
                    <p style={{ fontSize: '18px', fontWeight: 700, color: textPrimary, margin: 0 }}>
                      {summary.expiredUsers}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters Section */}
          <div
            className="rounded-lg px-4 py-2.5 mb-5"
            style={{
              background: cardBg,
              border: `1px solid ${cardBorder}`,
              boxShadow: mainShadow,
            }}
          >
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative flex-1 min-w-[160px] max-w-[280px]">
                <Search
                  size={12}
                  color={textMuted}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2"
                />
                <input
                  type="text"
                  placeholder={t('admin.accountsAnalysis.searchPlaceholder') || 'Search by name or email...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full"
                  style={{
                    ...inputStyle,
                    paddingLeft: '28px',
                    paddingRight: '8px',
                    paddingTop: '6px',
                    paddingBottom: '6px',
                  }}
                />
              </div>

              {/* Subscription Type */}
              <select
                value={subscriptionType}
                onChange={(e) => setSubscriptionType(e.target.value)}
                style={{
                  ...inputStyle,
                  padding: '6px 8px',
                  minWidth: '90px',
                  cursor: 'pointer',
                }}
              >
                <option value="">{t('admin.accountsAnalysis.allPlans') || 'All Plans'}</option>
                <option value="Trial">Trial</option>
                <option value="Starter">Starter</option>
                <option value="Pro">Pro</option>
                <option value="Elite">Elite</option>
              </select>

              {/* Status */}
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{
                  ...inputStyle,
                  padding: '6px 8px',
                  minWidth: '100px',
                  cursor: 'pointer',
                }}
              >
                <option value="">{t('admin.accountsAnalysis.allStatuses') || 'All Statuses'}</option>
                <option value="active">{t('admin.accountsAnalysis.active') || 'Active'}</option>
                <option value="trial">{t('admin.accountsAnalysis.trial') || 'Trial'}</option>
                <option value="pending">{t('admin.accountsAnalysis.pending') || 'Pending (no plan)'}</option>
                <option value="expired">{t('admin.accountsAnalysis.expired') || 'Expired'}</option>
                <option value="cancelled">{t('admin.accountsAnalysis.cancelled') || 'Cancelled'}</option>
              </select>

              {/* Date Range */}
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    padding: '6px 8px',
                    width: '120px',
                  }}
                />
                <span style={{ color: textMuted, fontSize: '12px' }}>-</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{
                    ...inputStyle,
                    padding: '6px 8px',
                    width: '120px',
                  }}
                />
              </div>

              {/* Clear Filters */}
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 transition-colors"
                style={{
                  padding: '6px 8px',
                  fontSize: '12px',
                  color: textSecondary,
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = isDark ? '#162456' : '#F3F4F6';
                  (e.currentTarget as HTMLElement).style.color = textPrimary;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                  (e.currentTarget as HTMLElement).style.color = textSecondary;
                }}
                title={t('common.clear') || 'Clear'}
              >
                <X size={10} />
                {t('common.clear') || 'Clear'}
              </button>
            </div>
          </div>

          {/* Users Table */}
          <div
            className="rounded-lg overflow-hidden"
            style={{
              background: tableBg,
              border: `1px solid ${cardBorder}`,
              boxShadow: mainShadow,
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Spinner size="large" text={t('common.loading') || 'Loading...'} />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20">
                <AlertTriangle size={36} color={isDark ? '#FCA5A5' : '#F87171'} style={{ marginBottom: '12px' }} />
                <p style={{ fontSize: '14px', color: textPrimary, fontWeight: 500, marginBottom: '4px' }}>
                  {t('admin.accountsAnalysis.errorOccurred') || 'An error occurred'}
                </p>
                <p style={{ fontSize: '12px', color: textSecondary, marginBottom: '16px' }}>{error}</p>
                <button
                  onClick={fetchUsers}
                  className="flex items-center gap-2 transition-colors text-xs font-medium"
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    background: refreshBtnBg,
                    color: refreshBtnColor,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = refreshBtnHoverBg;
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = refreshBtnBg;
                  }}
                >
                  <RefreshCcw size={14} />
                  {t('common.retry') || 'Retry'}
                </button>
              </div>
            ) : users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Inbox size={36} color={textMuted} style={{ marginBottom: '12px' }} />
                <p style={{ fontSize: '14px', color: textSecondary }}>
                  {t('admin.accountsAnalysis.noUsersFound') || 'No users found'}
                </p>
              </div>
            ) : (
              <>
                <div className={`overflow-x-auto ${isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}`}>
                  <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorderColor}` }}>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {t('admin.accountsAnalysis.user') || 'User'}
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {t('admin.accountsAnalysis.subscription') || 'Subscription'}
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('accountsCount')}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background = isDark ? '#162456' : '#F1F5F9';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                          }}
                        >
                          <div className="flex items-center gap-1">
                            {t('admin.accountsAnalysis.accounts') || 'Accounts'}
                            {sortBy === 'accountsCount' && (
                              sortOrder === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                            )}
                          </div>
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {t('admin.accountsAnalysis.activity') || 'Activity'}
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {t('admin.accountsAnalysis.jobs') || 'Jobs'}
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'left',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            cursor: 'pointer',
                          }}
                          onClick={() => handleSort('created_on')}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLElement).style.background = isDark ? '#162456' : '#F1F5F9';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLElement).style.background = 'transparent';
                          }}
                        >
                          <div className="flex items-center gap-1">
                            {t('admin.accountsAnalysis.joined') || 'Joined'}
                            {sortBy === 'created_on' && (
                              sortOrder === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />
                            )}
                          </div>
                        </th>
                        <th
                          style={{
                            padding: '12px 16px',
                            textAlign: 'center',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: textSecondary,
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                          }}
                        >
                          {t('admin.accountsAnalysis.actions') || 'Actions'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr
                          key={user._id}
                          className="transition-colors"
                          style={{
                            background: hoveredRow === user._id ? tableRowHoverBg : 'transparent',
                            borderBottom: `1px solid ${tableBorderColor}`,
                          }}
                          onMouseEnter={() => setHoveredRow(user._id)}
                          onMouseLeave={() => setHoveredRow(null)}
                        >
                          {/* User Info */}
                          <td style={{ padding: '12px 16px' }}>
                            <div className="flex items-center gap-3">
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0"
                                style={{ background: isDark ? '#314158' : '#E5E7EB' }}
                              >
                                {user.profilePicture ? (
                                  <img src={user.profilePicture} alt={user.name} className="w-full h-full object-cover" />
                                ) : (
                                  <User size={16} color={textSecondary} />
                                )}
                              </div>
                              <div>
                                <p style={{ fontSize: '12px', fontWeight: 600, color: textPrimary, margin: 0 }}>
                                  {user.name}
                                </p>
                                <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                                  {user.email}
                                </p>
                              </div>
                            </div>
                          </td>

                          {/* Subscription
                              Special case: status === 'pending' means the user signed up but
                              never completed Stripe checkout (no plan chosen yet). Showing
                              the default "Trial" subscription name would be misleading, so
                              we render "No plan · pending" instead. */}
                          <td style={{ padding: '12px 16px' }}>
                            <span
                              className="inline-flex items-center rounded text-xs font-medium"
                              style={{
                                padding: '2px 8px',
                                borderRadius: '4px',
                                ...getSubscriptionBadgeStyle(user.status === 'pending' ? 'pending' : user.subscription.type),
                              }}
                            >
                              {user.status === 'pending'
                                ? 'No plan · pending'
                                : `${user.subscription.type} · ${user.status}`}
                            </span>
                          </td>

                          {/* Accounts */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: '12px' }}>
                              <p style={{ fontWeight: 600, color: textPrimary, margin: 0 }}>
                                {user.accounts.total} total
                              </p>
                              <p style={{ color: textSecondary, margin: 0 }}>
                                <span style={{ color: activeColor }}>{user.accounts.active} active</span>
                                {user.accounts.locked > 0 && (
                                  <span style={{ color: lockedColor }}> / {user.accounts.locked} locked</span>
                                )}
                              </p>
                            </div>
                          </td>

                          {/* Activity */}
                          <td style={{ padding: '12px 16px' }}>
                            <div className="grid grid-cols-3 gap-1" style={{ fontSize: '12px' }}>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <Heart size={12} color={activityColors.likes} />
                                {user.activitySummary.totalLikes}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <MessageCircle size={12} color={activityColors.comments} />
                                {user.activitySummary.totalComments}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <UserPlus size={12} color={activityColors.follows} />
                                {user.activitySummary.totalFollows}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <Mail size={12} color={activityColors.messages} />
                                {user.activitySummary.totalMessages}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <Film size={12} color={activityColors.stories} />
                                {user.activitySummary.totalStories}
                              </span>
                              <span className="flex items-center gap-1" style={{ color: textSecondary }}>
                                <FileText size={12} color={activityColors.posts} />
                                {user.activitySummary.totalPosts}
                              </span>
                            </div>
                          </td>

                          {/* Jobs */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: '12px' }}>
                              <p style={{ fontWeight: 600, color: textPrimary, margin: 0 }}>
                                {user.jobs.total} total
                              </p>
                              <p style={{ color: textSecondary, margin: 0 }}>
                                <span style={{ color: successColor }}>{user.jobs.successRate.toFixed(1)}% success</span>
                              </p>
                            </div>
                          </td>

                          {/* Joined */}
                          <td style={{ padding: '12px 16px' }}>
                            <div style={{ fontSize: '12px' }}>
                              <p style={{ color: textPrimary, margin: 0 }}>{formatDate(user.joinDate)}</p>
                              <p style={{ color: textSecondary, margin: 0 }}>
                                Last: {formatDate(user.lastLogin)}
                              </p>
                            </div>
                          </td>

                          {/* Actions */}
                          <td style={{ padding: '12px 16px' }}>
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => openDetailsModal(user._id)}
                                className="transition-colors"
                                style={{
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: actionBtnStyles.details.color,
                                  background: hoveredAction === `details-${user._id}` ? actionBtnStyles.details.hoverBg : 'transparent',
                                }}
                                onMouseEnter={() => setHoveredAction(`details-${user._id}`)}
                                onMouseLeave={() => setHoveredAction(null)}
                                title={t('admin.accountsAnalysis.viewDetails') || 'View Details'}
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => openAccountsModal(user._id)}
                                className="transition-colors"
                                style={{
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: actionBtnStyles.accounts.color,
                                  background: hoveredAction === `accounts-${user._id}` ? actionBtnStyles.accounts.hoverBg : 'transparent',
                                }}
                                onMouseEnter={() => setHoveredAction(`accounts-${user._id}`)}
                                onMouseLeave={() => setHoveredAction(null)}
                                title={t('admin.accountsAnalysis.viewAccounts') || 'View Accounts'}
                              >
                                <Globe size={16} />
                              </button>
                              <button
                                onClick={() => openActivityLogsModal(user._id)}
                                className="transition-colors"
                                style={{
                                  padding: '6px',
                                  borderRadius: '4px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  color: actionBtnStyles.logs.color,
                                  background: hoveredAction === `logs-${user._id}` ? actionBtnStyles.logs.hoverBg : 'transparent',
                                }}
                                onMouseEnter={() => setHoveredAction(`logs-${user._id}`)}
                                onMouseLeave={() => setHoveredAction(null)}
                                title={t('admin.accountsAnalysis.viewLogs') || 'View Activity Logs'}
                              >
                                <List size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div
                  className="flex items-center justify-between flex-wrap gap-2"
                  style={{
                    padding: '12px 16px',
                    borderTop: `1px solid ${tableBorderColor}`,
                  }}
                >
                  <p style={{ fontSize: '12px', color: textSecondary, margin: 0 }}>
                    {t('admin.accountsAnalysis.showing') || 'Showing'} {(currentPage - 1) * limit + 1} -{' '}
                    {Math.min(currentPage * limit, totalUsers)} {t('admin.accountsAnalysis.of') || 'of'} {totalUsers}{' '}
                    {t('admin.accountsAnalysis.users') || 'users'}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="transition-colors"
                      style={{
                        padding: '6px 12px',
                        border: `1px solid ${paginationBtnBorder}`,
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: paginationBtnColor,
                        background: paginationBtnBg,
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? paginationBtnDisabledOpacity : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== 1) (e.currentTarget as HTMLElement).style.background = paginationBtnHoverBg;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = paginationBtnBg;
                      }}
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <span style={{ fontSize: '12px', color: textSecondary }}>
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="transition-colors"
                      style={{
                        padding: '6px 12px',
                        border: `1px solid ${paginationBtnBorder}`,
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: paginationBtnColor,
                        background: paginationBtnBg,
                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                        opacity: currentPage === totalPages ? paginationBtnDisabledOpacity : 1,
                      }}
                      onMouseEnter={(e) => {
                        if (currentPage !== totalPages) (e.currentTarget as HTMLElement).style.background = paginationBtnHoverBg;
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLElement).style.background = paginationBtnBg;
                      }}
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDetailsModal && selectedUserId && (
        <UserDetailsModal
          userId={selectedUserId}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedUserId(null);
          }}
        />
      )}

      {showAccountsModal && selectedUserId && (
        <UserAccountsModal
          userId={selectedUserId}
          onClose={() => {
            setShowAccountsModal(false);
            setSelectedUserId(null);
          }}
        />
      )}

      {showActivityLogsModal && selectedUserId && (
        <ActivityLogsModal
          userId={selectedUserId}
          onClose={() => {
            setShowActivityLogsModal(false);
            setSelectedUserId(null);
          }}
        />
      )}
    </div>
  );
};

// Wrapper component with Error Boundary
const AccountsAnalysis: React.FC = () => {
  return (
    <ErrorBoundary>
      <AccountsAnalysisContent />
    </ErrorBoundary>
  );
};

export default AccountsAnalysis;
